import React from 'react'
import { observer } from 'mobx-react-lite'
import { MediaObject } from 'types'
import { VideoDuration } from 'components/VideoDuration/VideoDuration'
import { MediaFileViewerModal } from 'components/MediaFileViewerModal/MediaFileViewerModal'
import { useModals } from 'almanac-ui/Modal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { isVideo } from 'components/ImagePickerModal/utils'
import { useRootStore } from 'stores/RootStore'
import { VideoPlayer } from '../VideoPlayer/VideoPlayer'
import Styles from './MediaRenderer.module.scss'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { EditInDesignerOverlay } from '../../components/EditInDesignerOverlay/EditInDesignerOverlay'
import { GeneratingOverlay } from '../GeneratingOverlay'
import cx from 'classnames'
import { DesignChannel } from 'components/NewFileModal/Types'

interface MediaRendererProps {
  mediaObject: MediaObject
  alt: string
  className?: string
  showVideoControls?: boolean
  videoFit?: 'contain' | 'cover'
  isActive?: boolean
  onClick?: () => void
  style?: React.CSSProperties
  isPressable?: boolean
  isGenerating?: boolean
  showHoverActionOverlay?: boolean
  almanacFileKey?: string
  onClose?: () => void
  hideAudioTransformButtons?: boolean
  allMediaObjects?: MediaObject[]
  channel?: DesignChannel
  caption?: string
}

export const MediaRenderer: React.FC<MediaRendererProps> = observer(
  ({
    mediaObject,
    alt,
    className,
    showVideoControls = false,
    videoFit,
    showHoverActionOverlay = false,
    almanacFileKey,
    isActive = true,
    isGenerating = false,
    onClick,
    style,
    isPressable = true,
    onClose,
    hideAudioTransformButtons = false,
    allMediaObjects,
    channel,
    caption,
  }) => {
    const { openModal } = useModals()
    const { isDesktop } = useResponsiveDesign()
    const { generationTimestampStore } = useRootStore()

    const isVideoContent = isVideo({ url: mediaObject.source_url, type: mediaObject.mime_type })
    const imageSrc = mediaObject.thumbnail_url || mediaObject.source_url

    const isDesignRegeneration = almanacFileKey ? !!generationTimestampStore.getStartedAt(almanacFileKey) : false
    const isDesignReplacing = almanacFileKey ? !!generationTimestampStore.getReplacingImageUrl(almanacFileKey) : false
    const showVideoPlayerLoading = isGenerating && !isDesignRegeneration
    const showGeneratingOverlay = (isGenerating && isDesignRegeneration) || isDesignReplacing

    const handleClick = () => {
      if (onClick) {
        onClick()
      } else if (isDesktop) {
        if (!isVideoContent || !showVideoControls) {
          openModal(MediaFileViewerModal, { file: mediaObject, editEnabled: false, showTitle: false })
        }
      }
    }

    return (
      <ConditionalWrapper
        condition={showHoverActionOverlay && almanacFileKey && !showGeneratingOverlay}
        wrapper={(children) => (
          <EditInDesignerOverlay
            channel={channel ?? DesignChannel.Post}
            almanacFileKey={almanacFileKey as string}
            borderRadius={0}
            position={isVideoContent ? 'top' : undefined}
            onClose={onClose}
            scrimOpacity={40}
            mediaObjects={allMediaObjects ?? [mediaObject]}
            caption={caption}
          >
            {children}
          </EditInDesignerOverlay>
        )}
      >
        <div
          className={cx(Styles.mediaContainer, { [Styles.coverVideo]: videoFit === 'cover' })}
          style={videoFit === 'cover' ? style : undefined}
        >
          {isVideoContent && showVideoControls ? (
            <VideoPlayer
              mediaObject={mediaObject}
              isActive={isActive && !isGenerating}
              className={cx(className, { [Styles.blurred]: showGeneratingOverlay })}
              almanacFileKey={almanacFileKey as string}
              isGenerating={showVideoPlayerLoading}
              hideAudioTransformButtons={hideAudioTransformButtons}
              videoFit={videoFit}
            />
          ) : (
            <img
              src={imageSrc}
              alt={alt}
              className={cx(className, Styles.image, {
                [Styles.nonPressable]: !isPressable,
                [Styles.blurred]: showGeneratingOverlay,
              })}
              style={style}
              onClick={isPressable ? handleClick : undefined}
            />
          )}
          {isVideoContent && !!mediaObject.duration && <VideoDuration duration={mediaObject.duration} />}
          <GeneratingOverlay isVisible={showGeneratingOverlay} designKey={almanacFileKey} />
        </div>
      </ConditionalWrapper>
    )
  },
)
